import { useEffect, useMemo, useState } from "react";
import {
  IAircraftConfigurationRevision,
  IFullAircraftConfiguration,
  IAircraft,
  IPosition,
  ICabinLayoutRef,
  IPaxWeightProfileRef,
} from "@vmosolutions/oc-interfaces";
import { Formik, Form, FormikProps, FormikHelpers, FieldArray } from "formik";
import {
  FormikTextField,
  FormikDateTimeField,
  FormikSelectField,
} from "../../../../MUI5FormikComponents/FormikWrappers";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { Box, FormLabel, Grid, Typography, useTheme } from "@mui/material";
import * as Yup from "yup";
import { AircraftSaveBar } from "../AircraftSaveBar";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import _ from "lodash";

const restFacilityOptions = [
  { label: "Class 1", value: 1 },
  { label: "Class 2", value: 2 },
  { label: "Class 3", value: 3 },
];

interface IRevisionProps {
  revisionDates?: number[];
  selectedRevision?: IAircraftConfigurationRevision;
  selectedConfiguration?: IFullAircraftConfiguration;
  selectedAircraft?: IAircraft;
  className?: string;
  configs: IFullAircraftConfiguration[] | null;
  updateAircraftConfigurationList?: (
    aircraftConfiguration?: IFullAircraftConfiguration,
    mode?: "create" | "update" | "delete"
  ) => void;
  updateAircraftList?: (ac?: IAircraft, mode?: "create" | "update") => void;
  stagedAircraft: IAircraft;
  setStagedAircraft: any;
  stagedConfig: IFullAircraftConfiguration[];
  setStagedConfig: any;
  setActiveAircraft: any;
  setConfigs: any;
  setCurrentConfig: any;
  setCurrentRevision: any;
  setForm: any;
}

const initialRevisionValues: IAircraftConfigurationRevision = {
  effectiveDate: null,
  _old_id: null,
  pxMaxCount: null,
  pxMaxWeight: null,
  maxCargo: null,
  maxFuel: null,
  minRampFuelForMaxPayload: null,
  emptyOperatingWeight: null,
  maxTakeoffWeight: null,
  zeroFuelWeight: null,
  maxLandingWeight: null,
  index: null,
  cruiseKnots: null,
  nonRevenueSeats: null,
  restFacilityClass: null,
  cabinLayoutId: null,
  paxWeightProfileId: null,
  defaultCrewCounts: [{ positionCode: "CA", count: 1 }],
};

type AircraftConfigurationRevisionForm = FormikProps<IAircraftConfigurationRevision>;

export const AircraftConfigurationRevisionForm = (props: IRevisionProps): JSX.Element => {
  const {
    revisionDates,
    selectedRevision,
    selectedConfiguration,
    selectedAircraft,
    configs,
    updateAircraftConfigurationList,
    stagedAircraft,
    setStagedAircraft,
    stagedConfig,
    setStagedConfig,
    updateAircraftList,
    setActiveAircraft,
    setConfigs,
    setCurrentConfig,
    setCurrentRevision,
    setForm,
  } = props;
  const dispatch = useDispatch();
  const positions: IPosition[] = useSelector((state: IRootState) => state.structures.customerStructure.positions).sort(
    (a: IPosition, b: IPosition) => (b.displayOrder > a.displayOrder ? -1 : 1)
  );
  const theme = useTheme();

  const effectiveDates = useMemo(() => {
    return selectedConfiguration?.revisions?.map((rev) => +rev.effectiveDate) ?? [];
  }, [selectedConfiguration]);

  const initialValues = useMemo(() => {
    return selectedRevision ? _.cloneDeep(selectedRevision) : initialRevisionValues;
  }, [selectedRevision]);

  // The two configuration catalogs (ENG-522), loaded once per mount. The registry collections are
  // authoritative - rows are provisioned by Vmo alongside the S3 document upload (ENG-522.js in
  // Vmo.OC.Db.Scripts), so what these return is exactly what may be offered here.
  const [cabinLayouts, setCabinLayouts] = useState<ICabinLayoutRef[]>([]);
  const [paxWeightProfiles, setPaxWeightProfiles] = useState<IPaxWeightProfileRef[]>([]);

useEffect(() => {
  let cancelled = false;
  void (async () => {
    try {
      const [layouts, profiles] = await Promise.all([
        AmplifyAPIWrapper.Get<ICabinLayoutRef[]>("/cabinLayouts"),
        AmplifyAPIWrapper.Get<IPaxWeightProfileRef[]>("/paxWeightProfiles"),
      ]);
      if (cancelled) return;
      setCabinLayouts(layouts ?? []);
      setPaxWeightProfiles(profiles ?? []);
    } catch (err) {
      console.error("Failed to load cabin layout / pax weight profile catalogs", err);
    }
  })();
  return () => { cancelled = true; };
}, []);

  // Withdrawn (inactive) entries and values the catalog has never heard of are still shown when a
  // revision carries them - hiding them would display a linked revision as unlinked, and clearing
  // someone's selection silently is worse than labelling it.
  const cabinLayoutOptions = useMemo(() => {
    const options = cabinLayouts
      .filter((layout) => layout.active)
      .map((layout) => ({
        label: layout.seatCount != null ? `${layout.name} (${layout.seatCount} seats)` : layout.name,
        value: layout.layoutId,
      }));
    const current = initialValues.cabinLayoutId;
    if (current && !options.some((opt) => opt.value === current)) {
      const withdrawn = cabinLayouts.find((layout) => layout.layoutId === current);
      options.push({
        label: withdrawn ? `${withdrawn.name} (inactive)` : `${current} (not in catalog)`,
        value: current,
      });
    }
    return options;
  }, [cabinLayouts, initialValues.cabinLayoutId]);

  const paxWeightProfileOptions = useMemo(() => {
    const options = paxWeightProfiles
      .filter((profile) => profile.active)
      .map((profile) => ({
        label: profile.carsSubpart ? `${profile.name} (${profile.carsSubpart})` : profile.name,
        value: profile.profileId,
      }));
    const current = initialValues.paxWeightProfileId;
    if (current && !options.some((opt) => opt.value === current)) {
      const withdrawn = paxWeightProfiles.find((profile) => profile.profileId === current);
      options.push({
        label: withdrawn ? `${withdrawn.name} (inactive)` : `${current} (not in catalog)`,
        value: current,
      });
    }
    return options;
  }, [paxWeightProfiles, initialValues.paxWeightProfileId]);

  // Configuration revision validation with YUP
  const ConfigurationRevisionSchema = Yup.object().shape({
    effectiveDate: Yup.number()
      .required("Please enter an effective date")
      .nullable()
      .notOneOf(effectiveDates, "Effective Date is already in use."),
    pxMaxCount: Yup.number()
      .required()
      .min(0, "Please enter a positive number")
      .typeError("Please enter a valid number")
      .nullable(),
    pxMaxWeight: Yup.number()
      .required()
      .min(0, "Please enter a positive number")
      .typeError("Please enter a valid number")
      .nullable(),
    maxCargo: Yup.number()
      .required()
      .min(0, "Please enter a positive number")
      .typeError("Please enter a valid number")
      .nullable(),
    maxFuel: Yup.number()
      .required()
      .min(0, "Please enter a positive number")
      .typeError("Please enter a valid number")
      .nullable(),
    minRampFuelForMaxPayload: Yup.number()
      .required()
      .min(0, "Please enter a positive number")
      .typeError("Please enter a valid number")
      .nullable(),
    emptyOperatingWeight: Yup.number()
      .required()
      .min(0, "Please enter a positive number")
      .typeError("Please enter a valid number")
      .nullable(),
    maxTakeoffWeight: Yup.number()
      .required()
      .min(0, "Please enter a positive number")
      .typeError("Please enter a valid number")
      .nullable(),
    zeroFuelWeight: Yup.number()
      .required()
      .min(0, "Please enter a positive number")
      .typeError("Please enter a valid number")
      .nullable(),
    maxLandingWeight: Yup.number()
      .required()
      .min(0, "Please enter a positive number")
      .typeError("Please enter a valid number")
      .nullable(),
    index: Yup.number().required().typeError("Please enter a valid number").nullable(),
    cruiseKnots: Yup.number()
      .required()
      .min(0, "Please enter a positive number")
      .typeError("Please enter a valid number")
      .nullable(),
    nonRevenueSeats: Yup.number()
      .required()
      .min(0, "Please enter a positive number")
      .typeError("Please enter a valid number")
      .nullable(),
    restFacilityClass: Yup.number().nullable().oneOf([1, 2, 3, null], "Must be 1, 2, or 3"),
    cabinLayoutId: Yup.string().nullable(),
    paxWeightProfileId: Yup.string().nullable(),
    _old_id: Yup.string().nullable(),
    comments: Yup.string(),
    defaultCrewCounts: Yup.array().of(
      Yup.object().shape({
        count: Yup.number()
          .nullable()
          .min(0, "Negative crew member counts as not allowed")
          .when("positionCode", {
            is: "CA",
            then: Yup.number().min(1, "At least one captain is required"),
          }),
      })
    ),
  });

  const handleRevisionSubmit = (
    values: IAircraftConfigurationRevision,
    helpers: FormikHelpers<IAircraftConfigurationRevision>
  ): void => {
    const newValues = _.cloneDeep(values);

    if (stagedConfig.length > 0) {
      const newStagedConfig = _.cloneDeep(stagedConfig);
      newStagedConfig[0].revisions.push(newValues);
      setStagedConfig(newStagedConfig);
    }
    const newSelectedConfiguration = _.cloneDeep(selectedConfiguration);
    newValues.defaultCrewCounts = newValues.defaultCrewCounts.filter((rev) => rev.count !== null);
    newSelectedConfiguration.revisions.push(newValues);
    handleConfigSubmit(newSelectedConfiguration, helpers);
    setCurrentRevision(newValues);
  };

  const handleConfigSubmit = async (
    values: IFullAircraftConfiguration,
    helpers: FormikHelpers<IAircraftConfigurationRevision>
  ): Promise<void> => {
    const { setSubmitting, setStatus, resetForm } = helpers;
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Saving Aircraft, Configuration and Revision...",
      successMessage: "Aircraft, Configuration and Revision Successfully Created.",
      errorMessage: "Error: Unable to Create Aircraft",
    };

    //Save all the staged work.
    if (!selectedConfiguration?._id) {
      //Save Aircraft - update it after getting config saved.
      let ACResponse;
      if (stagedAircraft) {
        const AC = { ...stagedAircraft };
        AC._id = null;
        ACResponse = await AmplifyAPIWrapper.Post<IAircraft>("/aircraft", AC);
      } else {
        ACResponse = selectedAircraft;
      }
      if (ACResponse?._id) {
        //Save Configuration
        const config = { ...values };
        config.aircraftId = ACResponse?._id;
        const ConfigResponse = await AmplifyAPIWrapper.Post<IFullAircraftConfiguration>(
          "/aircraftConfiguration",
          config
        );
        if (ConfigResponse?._id) {
          resetForm();
          setStatus({ success: true });
          effectiveDates.push(ConfigResponse.revisions[0].effectiveDate as number);

          //Update initial air craft for the default config.
          ACResponse.defaultConfig = { ...ConfigResponse };
          const response = await AmplifyAPIWrapper.Put<IAircraft>(
            `/aircraft/${ACResponse._id}`,
            ACResponse,
            notifications
          );
          if (response?._id) {
            //get rid of staged details and reset form to aircraft.
            setStagedConfig([]);
            setStagedAircraft();
            setForm("aircraft");
            if (updateAircraftList) updateAircraftList(response);
            setConfigs([]);
            setActiveAircraft(response);
            setStatus({ success: true });
            setSubmitting(false);
            resetForm();
          }
        } else {
          setStatus({ success: false });
        }
      }
    } else {
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: "Saving Aircraft Configuration Revision...",
        successMessage: "Aircraft Configuration Revision Successfully Created.",
        errorMessage: "Error: Unable to Create Aircraft Configuration Revision",
      };
      const ConfigResponse = await AmplifyAPIWrapper.Put<IFullAircraftConfiguration>(
        `/aircraftConfiguration/${values._id}`,
        values,
        notifications
      );
      if (ConfigResponse?._id) {
        if (updateAircraftConfigurationList) updateAircraftConfigurationList(values, "update");
        setStatus({ success: true });
        setCurrentConfig(values);
        resetForm();
      } else {
        setStatus({ success: false });
      }
      setSubmitting(false);
    }
  };
  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleRevisionSubmit}
      validationSchema={ConfigurationRevisionSchema}
    >
      {(AircraftConfigurationRevisionForm: AircraftConfigurationRevisionForm) => {
        const { dirty, isSubmitting, handleSubmit, isValid } = AircraftConfigurationRevisionForm;
        const checkRevisionDate = (date: number): any => {
          if (typeof revisionDates !== "undefined" && revisionDates?.length > 0 && revisionDates.includes(date)) {
            // Has to be wrapped inside a settimeout so that it fires after the normal validation routines.
            setTimeout(() => {
              AircraftConfigurationRevisionForm.setFieldError(
                "effectiveDate",
                "there is already a revision om this Date"
              );
            }, 0);
          }
          return date;
        };
        return (
          <Form>
            <Grid container>
              <Grid item xs={12}>
                <AircraftSaveBar
                  configs={configs}
                  aircraft={selectedAircraft}
                  currentConfig={selectedConfiguration}
                  currentRevision={initialValues}
                  mode={"revision"}
                  disabled={!isValid || isSubmitting || !dirty}
                  hasError={!isValid}
                  dirty={dirty}
                  saveAirCraftConfigRevision={handleSubmit}
                  stagedAircraft={stagedAircraft}
                  stagedConfig={stagedConfig}
                />
              </Grid>
              <Grid container item xs={12} sx={{ overflowY: "auto", maxHeight: "calc(100svh - 290px)" }}>
                <Grid item xs={7}>
                  <Typography variant="subtitle2">Required Fields</Typography>
                  <FormikDateTimeField
                    parse={checkRevisionDate}
                    label="Effective Date (UTC)"
                    name="effectiveDate"
                    isEpoch={true}
                  />
                  <FormikTextField label="Max Passenger Count" name="pxMaxCount" type="number" />
                  <FormikTextField label="Max Passenger Weight" name="pxMaxWeight" type="number" />
                  <FormikTextField label="Max Cargo Weight" name="maxCargo" type="number" />
                  <FormikTextField label="Max Fuel Weight" name="maxFuel" type="number" />
                  <FormikTextField
                    label="Min Ramp Fuel for Max Payload Weight"
                    name="minRampFuelForMaxPayload"
                    type="number"
                  />
                  <FormikTextField label="Empty Operating Weight" name="emptyOperatingWeight" type="number" />
                  <FormikTextField label="Max TakeOff Weight" name="maxTakeoffWeight" type="number" />
                  <FormikTextField label="Zero Fuel Weight" name="zeroFuelWeight" type="number" />
                  <FormikTextField label="Max Landing Weight" name="maxLandingWeight" type="number" />
                  <FormikTextField label="Index" name="index" type="number" />
                  <FormikTextField label="Cruise Speed (kts)" name="cruiseKnots" type="number" />
                  <FormikTextField label="Non-Revenue Seats" name="nonRevenueSeats" type="number" />
                  <FormikSelectField
                    name="restFacilityClass"
                    label="Rest Facility Class"
                    options={restFacilityOptions}
                    format={(value) => restFacilityOptions.find((opt) => opt.value === value) ?? null}
                    parse={(option) => option?.value ?? null}
                    isClearable
                    placeholder="Select class..."
                  />
                  <Box
                    sx={{
                      width: "100%",
                      my: 0.3,
                      ".MuiFormLabel-root": {
                        padding: "4px 10px",
                        fontSize: "0.8rem !important",
                        color: `${theme.custom.forms.labels} !important`,
                      },
                    }}
                  >
                    <Grid container sx={{ display: "flex", alignItems: "start" }}>
                      <Grid
                        item
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          textAlign: "right",
                        }}
                        xs
                      >
                        <FormLabel>Default Crew Counts</FormLabel>
                      </Grid>
                      <Grid item xs={7} container>
                        {positions.map((pos, posIdx) => {
                          const defaultCounts = [...AircraftConfigurationRevisionForm.values.defaultCrewCounts];
                          const posValueIdx = defaultCounts.findIndex((dcc: any) => {
                            return pos.title === dcc.positionCode;
                          });
                          return (
                            <Grid item xs={12} key={posIdx}>
                              <FormikTextField
                                className="field textfield position_count"
                                label={`${pos.description} (${pos.title})`}
                                name={`defaultCrewCounts.${posValueIdx}.count`}
                                type="number"
                                value={posValueIdx > -1 ? defaultCounts[posValueIdx].count : ""}
                                onChange={(value: any) => {
                                  if (posValueIdx > -1) {
                                    const val = parseInt(value);
                                    if (!isNaN(val) && val > 0) {
                                      defaultCounts[posValueIdx].count = val;
                                    } else {
                                      defaultCounts.splice(posValueIdx, 1);
                                    }
                                  } else {
                                    defaultCounts.push({ positionCode: pos.title, count: value });
                                  }
                                  AircraftConfigurationRevisionForm.setFieldValue(
                                    "defaultCrewCounts",
                                    defaultCounts,
                                    true
                                  );
                                }}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item xs={5}>
                  <Typography variant="subtitle2">Optional Fields</Typography>
                  <FormikTextField label="Flight Plan Id" name="FP_id" />
                  {/* S3-backed catalogs (ENG-522). Cleared means the EFB feature hides on this
                      config - the correct state for CARGO revisions, hence clearable. */}
                  <FormikSelectField
                    name="cabinLayoutId"
                    label="Cabin Layout"
                    options={cabinLayoutOptions}
                    format={(value) => cabinLayoutOptions.find((opt) => opt.value === value) ?? null}
                    parse={(option) => option?.value ?? null}
                    isClearable
                    placeholder="None (no cabin seat map)"
                  />
                  <FormikSelectField
                    name="paxWeightProfileId"
                    label="Pax Weight Profile"
                    options={paxWeightProfileOptions}
                    format={(value) => paxWeightProfileOptions.find((opt) => opt.value === value) ?? null}
                    parse={(option) => option?.value ?? null}
                    isClearable
                    placeholder="None (no estimated pax weights)"
                  />
                  <FormikTextField label="Comments" name="comments" multiline={true} minRows={3} maxRows={5} />
                </Grid>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
